import { BACKEND_URL } from "@/lib/api";
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";

const API = `${BACKEND_URL}/api`;
const PING_INTERVAL_MS = 25000; // server TTL is 60s, so ping every 25s comfortably stays alive

/**
 * <LiveReaders page="pricing" /> — dual-purpose social-proof component:
 *   - POSTs a heartbeat ping every 25s with a sessionStorage-anchored fingerprint
 *   - GETs the live count + reservations-this-week and renders a slim banner
 *
 * Strict opt-out: respects DNT and Storage exceptions (sessionStorage may throw
 * in some incognito contexts). If anything goes wrong it renders nothing.
 */
function getFingerprint() {
  try {
    const KEY = "cxs-fp";
    let fp = sessionStorage.getItem(KEY);
    if (!fp) {
      fp = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(KEY, fp);
    }
    return fp;
  } catch (_) {
    return null;
  }
}

export default function LiveReaders({ page, variant = "default", tone = "light" }) {
  const [data, setData] = useState({ readers: 0, reservations_7d: 0 });
  const [visible, setVisible] = useState(false);
  const fpRef = useRef(getFingerprint());

  useEffect(() => {
    if (!fpRef.current) return;
    let cancelled = false;

    const ping = async () => {
      try {
        await axios.post(`${API}/public/page-ping`, { page, fingerprint: fpRef.current });
      } catch (_) { /* ignore — server may rate-limit */ }
    };

    const fetchCounters = async () => {
      try {
        const { data } = await axios.get(`${API}/public/live-counters`, { params: { page } });
        if (!cancelled) {
          setData({ readers: data.readers || 0, reservations_7d: data.reservations_7d || 0 });
          setVisible(true);
        }
      } catch (_) { /* ignore */ }
    };

    ping();
    fetchCounters();
    const tPing = setInterval(ping, PING_INTERVAL_MS);
    const tFetch = setInterval(fetchCounters, 12000);

    return () => { cancelled = true; clearInterval(tPing); clearInterval(tFetch); };
  }, [page]);

  if (!visible) return null;
  const readers = data.readers || 0;
  const resv = data.reservations_7d;

  // Don't show the pill when there's nothing socially proven (true zero traffic + zero recent reservations).
  // The inline variant still shows so the layout doesn't shift.
  const hasSignal = readers > 0 || resv > 0;
  if (variant === "default" && !hasSignal) return null;

  // For display: show actual count, but never show "0 reading now" — that's the current viewer
  const readersDisplay = Math.max(1, readers);

  if (variant === "inline") {
    const baseColor = tone === "dark" ? "text-slate-300" : "text-slate-500";
    const sepColor = tone === "dark" ? "text-slate-600" : "text-slate-300";
    return (
      <div className={`inline-flex items-center gap-2 text-[11px] font-mono ${baseColor}`} data-testid="live-readers-inline">
        <span className="relative flex items-center justify-center size-2 shrink-0">
          <span className="absolute inline-flex h-full w-full rounded-full bg-[#22D3A6] opacity-75 animate-ping" />
          <span className="relative inline-flex rounded-full size-2 bg-[#22D3A6]" />
        </span>
        <span className="tabular-nums">{readersDisplay}</span> reading now
        {resv > 0 && (<>
          <span className={sepColor}>·</span>
          <span className="tabular-nums">{resv}</span> reserved this week
        </>)}
      </div>
    );
  }

  // Default: floating pill, bottom-right, subtle
  return (
    <div
      className="fixed bottom-5 left-5 z-30 hidden md:inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3 py-2 text-[12px] shadow-soft animate-in fade-in slide-in-from-bottom-2 duration-500"
      data-testid="live-readers-pill"
    >
      <span className="relative flex items-center justify-center size-2 shrink-0">
        <span className="absolute inline-flex h-full w-full rounded-full bg-[#22D3A6] opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full size-2 bg-[#22D3A6]" />
      </span>
      <span className="text-slate-700">
        <span className="font-semibold tabular-nums text-slate-900">{readersDisplay}</span> {readersDisplay === 1 ? "person is" : "people are"} reading this now
      </span>
      {resv > 0 && (
        <span className="text-slate-500 pl-2 border-l border-slate-200 ml-1">
          <span className="font-semibold tabular-nums text-slate-900">{resv}</span> reserved this week
        </span>
      )}
    </div>
  );
}
